import React from 'react';
import styled from 'styled-components';
import {
  columns,
  columnsWithNumbers,
  oneSizeColumns,
} from '~/common/constants/sizing';
import { Table } from '~/components/table';
import { TableEntity } from '~/types/sizings';
import gsap from 'gsap';
import { colors } from '~/theme/colors';
import { fontFamilies } from '~/theme/typography';

interface ISizeTableProps {
  data?: TableEntity;
}

const convertCmToInch = (data: (string | number)[][]) => {
  return data.map((row) => {
    return row.map((cell) => {
      if (typeof cell === 'string') return cell.replace('CM', 'IN');
      return ((cell as number) / 2.54).toFixed(2);
    });
  });
};

const SizeTable = ({ data }: ISizeTableProps) => {
  const [metric, setMetric] = React.useState<'cm' | 'inch'>('cm');
  const toggleRef = React.useRef(null);
  if (!data) return null;

  const changeMetric = (newMetric: 'cm' | 'inch') => {
    setMetric(newMetric);
    gsap.to(toggleRef.current, {
      x: newMetric === 'cm' ? 0 : '98%',
      duration: 0.05,
      ease: 'power4.inOut',
    });
  };

  return (
    <Container>
      <TableWrapper>
        <Table
          rows={metric === 'cm' ? data.sizing : convertCmToInch(data.sizing)}
          keyExtension={data.title}
          columns={
            data.sizing[0].length === 2
              ? oneSizeColumns
              : data.title === 'denim jeans' || data.title === 'wide leg jeans'
                ? columnsWithNumbers
                : columns
          }
        />
      </TableWrapper>
      <ToggleContainer>
        <Slider ref={toggleRef} />
        <ToggleButton
          active={metric === 'cm'}
          id="cm"
          onClick={() => changeMetric('cm')}
        >
          <p>METRIC</p>
        </ToggleButton>

        <ToggleButton
          active={metric === 'inch'}
          id="in"
          onClick={() => changeMetric('inch')}
        >
          <p>IMPERIAL</p>
        </ToggleButton>
      </ToggleContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const ToggleContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  position: relative;
  border: 0.0625rem solid ${({ theme }) => theme.colors.white};
  border-radius: 1rem;
  padding: 0.1rem;
  width: fit-content;

  @media ${({ theme }) => theme.breakpoints.sm} {
    width: 100%;
  }
`;

const ToggleButton = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  padding: 0.75rem 1rem;
  -webkit-tap-highlight-color: ${colors.transparent};

  p {
    color: ${({ active }) => (active ? colors.white : colors.greyDisabled2)};
    font-family: ${fontFamilies.CircularStd};
    font-size: 1rem;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.03125rem;
    text-transform: uppercase;
    transition: color 0.3s ease;
    margin: 0;

    &:hover {
      color: ${({ theme, active }) => !active && theme.colors.textHover};
    }
  }
`;

const Slider = styled.div`
  position: absolute;
  top: 0.0625rem;
  left: 0.0625rem;
  width: 50%;
  height: calc(100% - 0.125rem);
  z-index: 1;
  background-color: ${colors.greyDisabled2};
  border-radius: 1rem;
  transition: all 0.5s ease-in-out;
`;

export default SizeTable;
